import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { configService } from '../services/configService';

const { FiCheck, FiArrowRight } = FiIcons;

const getValidDate = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getActivePrice = (config, now) => {
  const tiers = [1, 2, 3, 4, 5]
    .map((number) => ({
      price: config[`tier${number}_price`],
      date: getValidDate(config[`tier${number}_date`]),
    }))
    .filter((tier) => tier.price !== '' && tier.date);

  const activeTier = tiers.find((tier) => now < tier.date);

  return activeTier?.price || tiers[tiers.length - 1]?.price || '';
};

export default function Pricing() {
  const [config, setConfig] = useState(null);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await configService.getAll();
        setConfig(data);
      } catch (error) {
        console.error('Failed to load pricing config:', error);
      }
    };

    loadData();

    const syncTimer = setInterval(loadData, 60000);
    const clockTimer = setInterval(() => setNow(new Date()), 1000);

    return () => {
      clearInterval(syncTimer);
      clearInterval(clockTimer);
    };
  }, []);

  const currentPrice = useMemo(() => {
    if (!config) {
      return '';
    }

    return getActivePrice(config, now);
  }, [config, now]);

  const plans = [
    {
      name: 'Bazi Financial & Investment Course',
      amount: currentPrice,
      currency: 'thb',
      features: [
        'เข้าเรียนหลักสูตรเต็ม 2 วัน (19-20 ก.ย. 69)',
        'Workshop วิเคราะห์ดวงการเงินส่วนตัว',
        'สิทธิ์เข้าถึงซอฟต์แวร์ Bazi Financial',
        'เอกสารประกอบการเรียนระดับพรีเมียม',
        'สิทธิ์เข้ากลุ่ม VIP ปรึกษาหลังเรียนจบ',
      ],
    },
  ];

  return (
    <section id="pricing" className="section-padding bg-zinc-50 border-t border-zinc-200">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <span className="text-brand-red font-black uppercase tracking-[0.3em] text-xs mb-4 block">
            Secure Enrollment
          </span>
          <h2 className="text-4xl md:text-6xl font-black text-zinc-900 mb-6 tracking-tighter leading-tight">
            ลงทะเบียนเรียน <br /> <span className="text-brand-red">และเริ่มต้นวันนี้</span>
          </h2>
          <p className="text-xl text-zinc-500 font-medium italic">
            "การลงทุนในความรู้ คือการลงทุนที่ให้ผลตอบแทนดีที่สุด"
          </p>
        </div>

        <div className="flex justify-center">
          {plans.map((plan) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="w-full max-w-lg bg-white border-[6px] border-zinc-900 p-10 md:p-14 shadow-[30px_30px_0px_0px_rgba(211,47,47,1)] relative overflow-hidden"
            >
              <div className="absolute top-8 right-[-35px] bg-brand-red text-white py-1 px-12 rotate-45 font-black text-[10px] uppercase tracking-widest shadow-lg">
                Official
              </div>

              <div className="mb-10">
                <h3 className="text-2xl md:text-3xl font-black text-zinc-900 mb-6 uppercase tracking-tight leading-none">
                  {plan.name}
                </h3>
                <div className="flex items-baseline gap-3">
                  <span className="text-6xl md:text-7xl font-black text-brand-red tracking-tighter">
                    {plan.amount || 'กำลังโหลด'}
                  </span>
                  <span className="text-2xl font-black text-zinc-400 uppercase">
                    {plan.currency}
                  </span>
                </div>
              </div>

              <div className="space-y-5 mb-12">
                {plan.features.map((feature, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="mt-1 bg-zinc-100 p-1 rounded-full text-brand-red">
                      <SafeIcon icon={FiCheck} className="h-4 w-4" />
                    </div>
                    <span className="text-zinc-700 font-bold text-lg">{feature}</span>
                  </div>
                ))}
              </div>

              <a
                href="#registration"
                className="w-full py-6 bg-zinc-900 text-white font-black text-xl hover:bg-brand-red transition-all duration-500 uppercase tracking-[0.2em] flex items-center justify-center gap-4 group shadow-xl shadow-zinc-200"
              >
                สมัครเรียนทันที
                <SafeIcon
                  icon={FiArrowRight}
                  className="w-6 h-6 group-hover:translate-x-2 transition-transform"
                />
              </a>

              <p className="mt-6 text-center text-zinc-400 text-xs font-bold uppercase tracking-widest">
                Secure Payment Options Available
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}